/**
 * SideBar Component
 *
 * This component renders a sidebar with customizable top and bottom icons,
 * a logo, and a drawer for additional menu options. It also handles navigation
 * and unsaved data warnings.
 *
 * @param {Object} props - The props for the SideBar component.
 * @param {Array<{ label: string; Icon: React.ElementType; permission: string[] }>} [props.topIcons]
 *        - An array of objects representing the top icons in the sidebar.
 *        Each object contains:
 *        - `label`: The label for the icon.
 *        - `Icon`: The React component for the icon.
 *        - `permission`: An array of user roles allowed to see the icon.
 * @param {Array<{ label: string; Icon: React.ElementType; permission: string[] }>} [props.bottomIcons]
 *        - An array of objects representing the bottom icons in the sidebar.
 *        Each object contains:
 *        - `label`: The label for the icon.
 *        - `Icon`: The React component for the icon.
 *        - `permission`: An array of user roles allowed to see the icon.
 * @param {boolean} [props.unsavedData]
 *        - A flag indicating whether there is unsaved data that requires user attention.
 * @param {() => void} [props.unsavedDataAction]
 *        - A callback function to handle actions when there is unsaved data.
 * @param {string} [props.logo]
 *        - The URL or path to the logo image displayed in the sidebar header.
 *
 * @returns {JSX.Element | null} The rendered SideBar component or null if the current path is `/theme`.
 */
import * as React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useTheme } from '@mui/material/styles';

import SideDrawer from 'components/SideBar/Component/SideDrawer/SideDrawer';

import { RootState } from '../../store';
import SideBarList from '../SideBarList';

import { AppBarStyled, Divider, Header, IconListBox } from './styles';
import { MenuItem } from './types';

export default function SideBar({
  topIcons,
  bottomIcons,
  unsavedData,
  unsavedDataAction,
  logo,
}: {
  topIcons: { label: string; Icon: any; permission: string[] | string; drawer: boolean }[];
  bottomIcons: { label: string; Icon: any; permission: string[] | string; drawer: boolean }[];
  unsavedData: boolean;
  unsavedDataAction: (url: string) => void;
  logo: string;
}) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [currentMenu, setCurrentMenu] = React.useState<string>('');
  const [openDrawer, setOpenDrawer] = React.useState<boolean>(false);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const closeDrawer = () => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    )
      return;
    setCurrentMenu('');
    setOpenDrawer(false);
  };

  const handleMenuClick = (menuItem: MenuItem) => {
    setCurrentMenu(menuItem.label);
    if (!openDrawer && menuItem.drawer) setOpenDrawer(true);
    if (openDrawer && !menuItem.drawer) setOpenDrawer(false);
  };

  const handleHomeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    setCurrentMenu('');
    setOpenDrawer(false);

    if (unsavedData) {
      event.preventDefault();
      return unsavedDataAction('/');
    }
  };

  useEffect(() => {
    setCurrentMenu(pathname.split('/')[1].toUpperCase());
  }, [pathname]);

  if (pathname === '/theme') {
    return null;
  }

  return (
    <AppBarStyled>
      <Header sx={{ marginTop: '10px', marginBottom: theme.spacing(5) }}>
        <Link to={'/'} onClick={handleHomeClick}>
          <Avatar sx={{ scale: '.9' }} alt="Logo" src={logo} />
        </Link>
      </Header>
      {isAuthenticated && (
        <>
          <IconListBox>
            <List>
              {topIcons.map((menuItem) => {
                if (menuItem.permission === 'All' || menuItem.permission.includes(user.user_role)) {
                  return (
                    <SideBarList
                      active={currentMenu === menuItem.label}
                      key={`${menuItem.label}-topIcon`}
                      menuItem={menuItem}
                      handleClick={handleMenuClick}
                      unsavedDataAction={unsavedDataAction}
                      unsavedData={unsavedData}
                    />
                  );
                }

                return null;
              })}
            </List>
            <List>
              {bottomIcons.map((menuItem) => {
                if (menuItem.permission === 'All' || menuItem.permission.includes(user.user_role)) {
                  return (
                    <Box key={`${menuItem.label}-bottomIcon`}>
                      <SideBarList
                        active={currentMenu === menuItem.label}
                        menuItem={menuItem}
                        handleClick={handleMenuClick}
                        unsavedDataAction={unsavedDataAction}
                        unsavedData={unsavedData}
                      />
                      {menuItem.label === 'CLIENT' && <Divider variant="middle" />}
                    </Box>
                  );
                }

                return null;
              })}
            </List>
          </IconListBox>
          {openDrawer && <SideDrawer openDrawer={openDrawer} close={closeDrawer()} title={currentMenu} />}
        </>
      )}
    </AppBarStyled>
  );
}
